// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// safetyNumber derives an out-of-band verification code from two users' device-key
// fingerprints — the keys messages are actually encrypted to — so two people can
// confirm (in person / by scanning a QR) that neither side's keys were swapped by a
// MITM. It's symmetric (order-independent) so both ends see the same number, and
// changes if EITHER side's key set changes (a new device, a rotation, or an attack).
// Verification state is local to this device (the user attests "I compared these").

async function sha256Hex(s: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

// keyDigest is the stable per-user digest of their device-key fingerprints (normalised
// + sorted, so order/whitespace/case don't matter). The verified state stores this, so
// a later change is detectable.
export async function keyDigest(fingerprints: string[]): Promise<string> {
  const norm = fingerprints
    .map((f) => f.replace(/\s+/g, "").toUpperCase())
    .filter(Boolean)
    .sort();
  return sha256Hex(norm.join("|"));
}

// SAS_EMOJI is a 64-entry palette of visually distinct emoji (a power of two, so 6 bits
// of digest map to one emoji with NO modulo bias). Five of them encode 30 bits of the
// same safety-number digest — a Telegram-style quick-compare that's much faster to read
// aloud or eyeball than 60 digits, while staying a pure view of the identical material.
export const SAS_EMOJI: ReadonlyArray<{ glyph: string; name: string }> = [
  { glyph: "🐶", name: "dog" }, { glyph: "🐱", name: "cat" }, { glyph: "🦊", name: "fox" }, { glyph: "🦁", name: "lion" },
  { glyph: "🐴", name: "horse" }, { glyph: "🦄", name: "unicorn" }, { glyph: "🐮", name: "cow" }, { glyph: "🐷", name: "pig" },
  { glyph: "🐭", name: "mouse" }, { glyph: "🐰", name: "rabbit" }, { glyph: "🐻", name: "bear" }, { glyph: "🐨", name: "koala" },
  { glyph: "🐵", name: "monkey" }, { glyph: "🐔", name: "chicken" }, { glyph: "🐧", name: "penguin" }, { glyph: "🦅", name: "eagle" },
  { glyph: "🦉", name: "owl" }, { glyph: "🐸", name: "frog" }, { glyph: "🐢", name: "turtle" }, { glyph: "🐍", name: "snake" },
  { glyph: "🐙", name: "octopus" }, { glyph: "🐳", name: "whale" }, { glyph: "🦋", name: "butterfly" }, { glyph: "🐝", name: "bee" },
  { glyph: "🌵", name: "cactus" }, { glyph: "🌴", name: "palm tree" }, { glyph: "🌸", name: "blossom" }, { glyph: "🌹", name: "rose" },
  { glyph: "🌻", name: "sunflower" }, { glyph: "🍀", name: "clover" }, { glyph: "🍄", name: "mushroom" }, { glyph: "🍇", name: "grapes" },
  { glyph: "🍉", name: "watermelon" }, { glyph: "🍋", name: "lemon" }, { glyph: "🍌", name: "banana" }, { glyph: "🍎", name: "apple" },
  { glyph: "🍓", name: "strawberry" }, { glyph: "🍒", name: "cherries" }, { glyph: "🍑", name: "peach" }, { glyph: "🍍", name: "pineapple" },
  { glyph: "🥥", name: "coconut" }, { glyph: "🥕", name: "carrot" }, { glyph: "🌽", name: "corn" }, { glyph: "🍔", name: "burger" },
  { glyph: "🍕", name: "pizza" }, { glyph: "🍩", name: "donut" }, { glyph: "🎂", name: "cake" }, { glyph: "🧀", name: "cheese" },
  { glyph: "⚽", name: "ball" }, { glyph: "🏀", name: "basketball" }, { glyph: "🎸", name: "guitar" }, { glyph: "🎺", name: "trumpet" },
  { glyph: "🎈", name: "balloon" }, { glyph: "🎁", name: "gift" }, { glyph: "🔔", name: "bell" }, { glyph: "⭐", name: "star" },
  { glyph: "🌙", name: "moon" }, { glyph: "☀️", name: "sun" }, { glyph: "⚡", name: "lightning" }, { glyph: "❄️", name: "snowflake" },
  { glyph: "🔥", name: "fire" }, { glyph: "💧", name: "droplet" }, { glyph: "🌈", name: "rainbow" }, { glyph: "🚀", name: "rocket" },
];

// deriveEmojiSAS maps an arbitrary seed string to `count` emoji from SAS_EMOJI — a
// Short Authentication String. Used for per-call verification (the seed is the call's
// DTLS certificate fingerprints), so two people can confirm aloud that no one is
// man-in-the-middling the call's key exchange. 6 bits per emoji → no modulo bias.
export async function deriveEmojiSAS(seed: string, count = 5): Promise<{ glyph: string; name: string }[]> {
  const h = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode("zalmoxis-call-sas:" + seed)));
  return Array.from({ length: count }, (_, i) => SAS_EMOJI[h[i] & 0x3f]);
}

export interface SafetyNumber {
  digits: string; // 60 digits, 12 groups of 5 (Signal-style)
  groups: string[]; // the 12 groups, for display
  emojis: { glyph: string; name: string }[]; // 5-emoji quick-compare (same digest, Telegram-style)
  payload: string; // the QR payload (the combined digest) — both ends produce the same
  peerDigest: string; // the peer's key digest (what gets stored on "verified")
}

// deriveSafetyNumber computes the shared safety number from my + the peer's fingerprints.
export async function deriveSafetyNumber(myFingerprints: string[], peerFingerprints: string[]): Promise<SafetyNumber> {
  const mine = await keyDigest(myFingerprints);
  const peerDigest = await keyDigest(peerFingerprints);
  const [lo, hi] = mine < peerDigest ? [mine, peerDigest] : [peerDigest, mine]; // order-independent
  const payload = lo + hi;
  // 60 decimal digits from 36 bytes of hash material (12 × 3-byte windows mod 100000).
  const m1 = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(payload)));
  const m2 = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode("zalmoxis-sas:" + payload)));
  const mat = [...m1, ...m2]; // 64 bytes
  const groups: string[] = [];
  for (let i = 0; i < 12; i++) {
    const n = ((mat[i * 3] << 16) | (mat[i * 3 + 1] << 8) | mat[i * 3 + 2]) % 100000;
    groups.push(String(n).padStart(5, "0"));
  }
  // 5 emoji from the upper, digit-unused bytes — 6 bits each, no modulo bias (64 = 2^6).
  const emojis = [40, 44, 48, 52, 56].map((off) => SAS_EMOJI[mat[off] & 0x3f]);
  return { digits: groups.join(""), groups, emojis, payload, peerDigest };
}

// ---- verification state (local to this device) ----------------------------

function vKey(myUserID: string, peerUserID: string): string {
  return `zalmoxis.verified.${myUserID}.${peerUserID}`;
}

export type VerificationState = "verified" | "changed" | "unverified";

// verifiedPeerDigest returns the peer key-digest the user last marked verified, or null.
export function verifiedPeerDigest(myUserID: string, peerUserID: string): string | null {
  return localStorage.getItem(vKey(myUserID, peerUserID));
}

export function markVerified(myUserID: string, peerUserID: string, peerDigest: string): void {
  localStorage.setItem(vKey(myUserID, peerUserID), peerDigest);
}

export function clearVerified(myUserID: string, peerUserID: string): void {
  localStorage.removeItem(vKey(myUserID, peerUserID));
}

// verificationState compares the peer's CURRENT key digest to what was verified:
// "verified" (match), "changed" (verified before but keys differ now — re-verify), or
// "unverified" (never verified).
export function verificationState(myUserID: string, peerUserID: string, currentPeerDigest: string): VerificationState {
  const stored = verifiedPeerDigest(myUserID, peerUserID);
  if (!stored) return "unverified";
  return stored === currentPeerDigest ? "verified" : "changed";
}
